import { motion } from "framer-motion";
import { ArrowRight, ExternalLink, Sparkles, BookOpen, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const STAN_URL = "https://stan.store/Fayesteadman";

const products = [
  {
    title: "Free eBook",
    description: "Join my email list and get my free eBook. Never miss an update from me!",
    price: "Free",
    image: "https://assets.stanwith.me/live/msc/27075102/q2t1z/manifesting202501222330400000.png/webp/433x433/100/manifesting202501222330400000.webp?width=500",
    link: STAN_URL,
    buttonText: "Download Free",
    icon: BookOpen,
    tag: "Free Download",
  },
  {
    title: "Join My Membership",
    description: "Get exclusive how-to tips, weekly check-ins and live webinars with me! A supportive space for your personal growth journey.",
    price: "£9.99/mo",
    image: "https://assets.stanwith.me/live/msc/27075102/e8wth/file00000000b74c71f48e27dc6382b6b62e.png/webp/433x433/100/file00000000b74c71f48e27dc6382b6b62e.webp?width=500",
    link: STAN_URL,
    buttonText: "Join Now",
    icon: Users,
    tag: "Membership",
  },
  {
    title: "Smoothie Magic eBook",
    description: "9 delicious & nourishing smoothie + bowl recipes to fuel your glow. Sent straight to your inbox.",
    price: "£7.77",
    image: "https://assets.stanwith.me/live/msc/27075102/elj23/screenshot20250921184203gallery.jpg/webp/433x433/100/screenshot20250921184203gallery.webp?width=500",
    link: STAN_URL,
    buttonText: "Blend The Magic",
    icon: Sparkles,
    tag: "eBook",
  },
];

export default function ShopPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="shop-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-muted">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Digital Products & More
            </p>
            <h1 className="heading-xl mb-6" data-testid="shop-title">
              The <em className="italic">Shop</em>
            </h1>
            <p className="body-text text-muted-foreground max-w-lg mx-auto">
              Resources, guides, and memberships to support your wellness journey from the comfort of home.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="section-spacing">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {products.map((product, index) => (
              <motion.a
                key={product.title}
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="group block"
                data-testid={`shop-product-${index}`}
              >
                <div className="border border-border bg-background overflow-hidden transition-all duration-500 hover:shadow-lg hover:border-primary/30">
                  {/* Image */}
                  <div className="aspect-square relative overflow-hidden bg-muted">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary/90 text-primary-foreground px-3 py-1 text-[10px] tracking-[0.15em] uppercase">
                        {product.tag}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 lg:p-8">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-serif text-xl text-foreground group-hover:text-primary transition-colors">
                        {product.title}
                      </h3>
                      <span className="font-serif text-lg text-primary font-medium">
                        {product.price}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {product.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-primary font-medium">
                      {product.buttonText}
                      <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Visit Full Store */}
      <section className="section-spacing bg-muted">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Explore Everything
            </p>
            <h2 className="heading-lg mb-6">
              Visit the <em className="italic">Full Store</em>
            </h2>
            <p className="body-text text-muted-foreground max-w-md mx-auto mb-10">
              Browse all products, memberships, and free resources on my Stan Store.
            </p>
            <a
              href={STAN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="visit-store-btn"
            >
              <Button className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-7 text-xs tracking-[0.2em] uppercase btn-animate">
                Visit My Store
                <ExternalLink className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
